import {
  QUOTE,
  useObservableValue,
  useSubscription,
  wsScanToMap,
  type MinimalSubscriptionResponse,
  type Quote,
} from '@talos/kyoko';
import { useMemo } from 'react';
import { map, of, shareReplay, type Observable } from 'rxjs';

import type { RFQSweeperQuote } from '../RFQSweeper';

export interface QuoteSweepingQuoteMarkets {
  /** Representative parent RFQ quote. Used for shared display metadata like Symbol, PricingMode, and OrderQty. */
  representativeQuote: Quote;
  /** Executable quote/market pairs. Each item preserves the owning quote for its first quote market. */
  sweeperQuotes: RFQSweeperQuote[];
  /** Same filtered quote stream used to build the rows; cancel flows use it to wait for quote feedback. */
  quoteDataForSweepGroupObservable: Observable<MinimalSubscriptionResponse<Quote>>;
}

type QuoteSweepingQuoteMarketsData = Omit<QuoteSweepingQuoteMarkets, 'quoteDataForSweepGroupObservable'>;

/**
 * Returns the live quote subscription stream for a sweep group.
 *
 * Multiple actions in the sweeper need the same filtered quote stream: the panel builds
 * market rows from it, and cancel/confirm flows wait on quote feedback from it. Keeping
 * the websocket envelope intact lets downstream websocket pipes still handle removes,
 * initial-page resets, and pagination metadata correctly.
 */
export function useQuoteSweepingQuoteObservable(
  correlationID: string | undefined,
  tag: string
): Observable<MinimalSubscriptionResponse<Quote>> {
  const quoteRequest = useMemo(
    () =>
      correlationID
        ? {
            name: QUOTE,
            tag,
            SweepGroupID: correlationID,
          }
        : null,
    [correlationID, tag]
  );

  const { data: quoteDataObservable } = useSubscription<Quote>(quoteRequest, { loadAll: false });

  return useMemo<Observable<MinimalSubscriptionResponse<Quote>>>(() => {
    if (!correlationID) {
      return of({ data: [] });
    }

    return quoteDataObservable.pipe(
      map(json => ({
        ...json,
        data: json.data.filter(quote => quote.SweepGroupID === correlationID),
      })),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }, [correlationID, quoteDataObservable]);
}

/**
 * Subscribes to all quote rows for a sweep group ID and shapes them for the sweeper.
 *
 * The sweeper UI needs one representative quote for shared RFQ metadata, but execution
 * needs the exact parent quote for each selectable market so RFQID/QuoteID are correct.
 * This hook keeps both views of the data together and updates as quote rows stream in.
 */
export function useQuoteSweepingQuoteMarkets(correlationID: string | undefined): QuoteSweepingQuoteMarkets | undefined {
  const quoteDataForSweepGroupObservable = useQuoteSweepingQuoteObservable(
    correlationID,
    'useQuoteSweepingQuoteMarkets'
  );

  const quoteSweepingQuoteMarketsObservable = useMemo<Observable<QuoteSweepingQuoteMarketsData | undefined>>(() => {
    if (!correlationID) {
      return of(undefined);
    }

    return quoteDataForSweepGroupObservable.pipe(
      wsScanToMap({ getUniqueKey: quote => quote.RFQID, newMapEachUpdate: true }),
      map(quotesByRFQID => getQuoteSweepingQuoteMarkets([...quotesByRFQID.values()])),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }, [correlationID, quoteDataForSweepGroupObservable]);

  const quoteSweepingQuoteMarkets = useObservableValue(
    () => quoteSweepingQuoteMarketsObservable,
    [quoteSweepingQuoteMarketsObservable]
  );

  return useMemo(
    () =>
      quoteSweepingQuoteMarkets
        ? {
            ...quoteSweepingQuoteMarkets,
            quoteDataForSweepGroupObservable, // return this base obs too so consumers can reuse it
          }
        : undefined,
    [quoteDataForSweepGroupObservable, quoteSweepingQuoteMarkets]
  );
}

/**
 * Converts streamed quote rows into the RFQ sweeper data shape.
 *
 * Each quote row contributes its first market as the selectable market. If multiple rows
 * report the same market, keep the first one received so the displayed market list stays
 * unique and stable across websocket updates.
 */
function getQuoteSweepingQuoteMarkets(quotes: Quote[]): QuoteSweepingQuoteMarketsData | undefined {
  const quote = quotes.at(0);
  if (!quote) {
    return undefined;
  }

  const quotesByMarket = new Map<string, RFQSweeperQuote>();

  for (const quote of quotes) {
    const quoteMarket = quote.Markets.at(0);

    if (quoteMarket && !quotesByMarket.has(quoteMarket.Market)) {
      quotesByMarket.set(quoteMarket.Market, { quote, quoteMarket });
    }
  }

  return {
    representativeQuote: quote,
    sweeperQuotes: [...quotesByMarket.values()],
  };
}
